<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import {
  NAV_ICON_NAMES, NAV_PERMISSION_OPTIONS, NAV_ROUTE_OPTIONS,
  type NavIcon, type NavigationChild, type NavigationMenu
} from "@wjbk/workbench-contracts";
import { api } from "../api";
import { navigationIcon } from "../navigation-icons";

const menus = ref<NavigationMenu[]>([]);
const version = ref(0);
const selectedId = ref("");
const loading = ref(true);
const saving = ref(false);
const error = ref("");
const notice = ref("");
const selected = computed(() => menus.value.find((menu) => menu.id === selectedId.value));
const usedPaths = computed(() => new Set(menus.value.flatMap((menu) => menu.children.map((child) => child.path))));
const icons = NAV_ICON_NAMES;
const permissions = NAV_PERMISSION_OPTIONS;
const routes = NAV_ROUTE_OPTIONS;

async function load() {
  loading.value = true;
  error.value = "";
  try {
    const data = await api.navigationConfig();
    menus.value = structuredClone(data.menus);
    version.value = data.version;
    if (!menus.value.some((menu) => menu.id === selectedId.value)) selectedId.value = menus.value[0]?.id ?? "";
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : "读取菜单失败";
  } finally {
    loading.value = false;
  }
}

function addMenu() {
  const route = routes.find((item) => !usedPaths.value.has(item.path));
  if (!route) { error.value = "所有可用页面都已加入菜单"; return; }
  const id = `menu-${Date.now().toString(36)}`;
  menus.value.push({
    id, label: "新栏目", description: "栏目说明", icon: "Grid", order: (menus.value.length + 1) * 10,
    placement: "left", enabled: true,
    children: [{ id: `${id}-entry`, path: route.path, label: route.label, description: route.description,
      icon: "Grid", order: 10, enabled: true }]
  });
  selectedId.value = id;
  notice.value = "新栏目尚未保存";
}

function addChild(menu: NavigationMenu) {
  const route = routes.find((item) => !usedPaths.value.has(item.path));
  if (!route) { error.value = "没有未使用的页面入口"; return; }
  const id = `entry-${Date.now().toString(36)}`;
  menu.children.push({ id, path: route.path, label: route.label, description: route.description,
    icon: "Grid", order: (menu.children.length + 1) * 10, enabled: true });
  notice.value = "二级菜单尚未保存";
}

function moveMenu(menu: NavigationMenu, offset: number) {
  const ordered = [...menus.value].sort((a, b) => a.order - b.order);
  const index = ordered.findIndex((item) => item.id === menu.id);
  const target = index + offset;
  if (target < 0 || target >= ordered.length) return;
  [ordered[index], ordered[target]] = [ordered[target]!, ordered[index]!];
  ordered.forEach((item, position) => { item.order = (position + 1) * 10; });
  menus.value = ordered;
}

function moveChild(menu: NavigationMenu, child: NavigationChild, offset: number) {
  const ordered = [...menu.children].sort((a, b) => a.order - b.order);
  const index = ordered.findIndex((item) => item.id === child.id);
  const target = index + offset;
  if (target < 0 || target >= ordered.length) return;
  [ordered[index], ordered[target]] = [ordered[target]!, ordered[index]!];
  ordered.forEach((item, position) => { item.order = (position + 1) * 10; });
  menu.children = ordered;
}

function removeMenu(menu: NavigationMenu) {
  if (!confirm(`删除「${menu.label}」及其二级菜单？保存后生效。`)) return;
  menus.value = menus.value.filter((item) => item.id !== menu.id);
  selectedId.value = menus.value[0]?.id ?? "";
}

function removeChild(menu: NavigationMenu, child: NavigationChild) {
  if (menu.children.length === 1) { error.value = "一级菜单至少需要一个二级入口；可直接删除整个一级菜单"; return; }
  menu.children = menu.children.filter((item) => item.id !== child.id);
}

function clean(menusToSave: NavigationMenu[]): NavigationMenu[] {
  return menusToSave.map((menu) => ({ ...menu,
    icon: menu.icon as NavIcon,
    permissionCode: menu.permissionCode || undefined,
    systemCode: menu.systemCode || undefined,
    children: menu.children.map((child) => ({ ...child, permissionCode: child.permissionCode || undefined }))
  }));
}

async function save() {
  saving.value = true;
  error.value = "";
  notice.value = "";
  try {
    const result = await api.saveNavigationConfig(version.value, clean(menus.value));
    version.value = result.version;
    menus.value = structuredClone(result.menus);
    notice.value = "导航配置已保存，所有账号刷新后按各自权限显示。";
    window.dispatchEvent(new Event("wjbk:navigation-updated"));
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : "保存失败";
  } finally {
    saving.value = false;
  }
}

onMounted(load);
</script>

<template>
  <div class="nav-settings">
    <div class="nav-settings-intro">
      <div><h2>导航菜单配置</h2><p>设置栏目名称、SVG 图标、排序与位置。二级菜单自动跟随所属一级菜单；未授权的入口不会显示。</p></div>
      <div class="nav-settings-actions">
        <button type="button" class="subtle" :disabled="loading || saving" @click="load">重新载入</button>
        <button type="button" class="save" :disabled="loading || saving" @click="save">{{ saving ? "保存中…" : "保存配置" }}</button>
      </div>
    </div>
    <p v-if="error" class="nav-settings-error" role="alert">{{ error }}</p>
    <p v-if="notice" class="nav-settings-notice" role="status">{{ notice }}</p>
    <p v-if="loading" class="nav-settings-loading">正在读取导航配置…</p>
    <div v-else class="nav-settings-layout">
      <aside class="nav-settings-list" aria-label="一级菜单列表">
        <div class="list-title"><strong>一级菜单</strong><button type="button" @click="addMenu">＋ 新增</button></div>
        <button v-for="menu in [...menus].sort((a,b) => a.order-b.order)" :key="menu.id" type="button"
          class="menu-select" :class="{ selected: selectedId === menu.id }" @click="selectedId = menu.id">
          <span class="menu-select-icon"><component :is="navigationIcon(menu.icon)" /></span><span><strong>{{ menu.label }}</strong><small>{{ menu.placement === "top" ? "顶部导航" : "左侧导航" }} · {{ menu.children.length }} 项</small></span>
        </button>
      </aside>
      <section v-if="selected" class="nav-settings-editor" aria-label="菜单编辑器">
        <div class="editor-head"><div><h3>{{ selected.label }}</h3><p>一级菜单</p></div>
          <div class="order-actions"><button type="button" title="上移" @click="moveMenu(selected, -1)">↑</button><button type="button" title="下移" @click="moveMenu(selected, 1)">↓</button><button type="button" class="danger" @click="removeMenu(selected)">删除栏目</button></div>
        </div>
        <div class="editor-grid">
          <label>名称<input v-model.trim="selected.label" maxlength="24" /></label>
          <label>SVG 图标<span class="icon-select"><component :is="navigationIcon(selected.icon)" /><select v-model="selected.icon"><option v-for="icon in icons" :key="icon" :value="icon">{{ icon }}</option></select></span></label>
          <label class="wide">简短说明<input v-model.trim="selected.description" maxlength="100" /></label>
          <label>展示位置<select v-model="selected.placement"><option value="left">左侧导航</option><option value="top">顶部导航</option></select></label>
          <label>展示顺序<input v-model.number="selected.order" type="number" min="0" max="9999" /></label>
          <label>所需权限<select v-model="selected.permissionCode"><option value="">所有登录账号</option><option v-for="code in permissions" :key="code" :value="code">{{ code }}</option></select></label>
          <label>所属系统<select v-model="selected.systemCode"><option value="">工作台</option><option value="crmeb">商城</option><option value="bytedesk">客服</option></select></label>
          <label class="check"><input v-model="selected.enabled" type="checkbox" /> 显示该栏目</label>
        </div>
        <div class="child-heading"><div><h4>二级菜单</h4><p>使用预设页面入口，位置自动跟随一级菜单。</p></div><button type="button" @click="addChild(selected)">＋ 添加入口</button></div>
        <div v-for="child in [...selected.children].sort((a,b) => a.order-b.order)" :key="child.id" class="child-editor">
          <div class="child-top"><strong>{{ child.label }}</strong><div class="order-actions"><button type="button" title="上移" @click="moveChild(selected, child, -1)">↑</button><button type="button" title="下移" @click="moveChild(selected, child, 1)">↓</button><button type="button" class="danger" @click="removeChild(selected, child)">删除</button></div></div>
          <div class="editor-grid">
            <label>标题<input v-model.trim="child.label" maxlength="32" /></label>
            <label>目标页面<select v-model="child.path"><option v-for="option in routes" :key="option.path" :value="option.path" :disabled="usedPaths.has(option.path) && child.path !== option.path">{{ option.label }} · {{ option.path }}</option></select></label>
            <label>SVG 图标<span class="icon-select"><component :is="navigationIcon(child.icon)" /><select v-model="child.icon"><option v-for="icon in icons" :key="icon" :value="icon">{{ icon }}</option></select></span></label>
            <label>展示顺序<input v-model.number="child.order" type="number" min="0" max="9999" /></label>
            <label class="wide">一行说明<input v-model.trim="child.description" maxlength="100" /></label>
            <label>所需权限<select v-model="child.permissionCode"><option value="">跟随一级菜单</option><option v-for="code in permissions" :key="code" :value="code">{{ code }}</option></select></label>
            <label class="check"><input v-model="child.enabled" type="checkbox" /> 显示此入口</label>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<style scoped>
.nav-settings { max-width: 1440px; margin: 0 auto; }
.nav-settings-intro,.editor-head,.child-heading,.child-top,.list-title { display:flex; justify-content:space-between; align-items:center; gap:16px; }
.nav-settings-intro { margin-bottom:18px; }
.nav-settings h2 { margin:0 0 7px; font-size:20px; }.nav-settings h3 { margin:0; font-size:17px; }.nav-settings h4 { margin:0 0 4px; font-size:15px; }
.nav-settings p { margin:0; color:#768195; font-size:12px; line-height:1.6; }
.nav-settings button { cursor:pointer; border:1px solid #e1e5eb; background:#fff; color:#46536a; border-radius:8px; padding:8px 11px; }
.nav-settings button:focus-visible,.nav-settings input:focus-visible,.nav-settings select:focus-visible { outline:2px solid #8f2535; outline-offset:2px; }
.nav-settings button:disabled { opacity:.55; cursor:default; }
.nav-settings-actions,.order-actions { display:flex; gap:7px; }.nav-settings-actions .save { background:#922738; color:#fff; border-color:#922738; }
.nav-settings-error,.nav-settings-notice { padding:11px 14px; border-radius:9px; margin:10px 0 !important; }.nav-settings-error { color:#a32232 !important; background:#fff0f1; }.nav-settings-notice { color:#24724e !important; background:#eaf8f0; }
.nav-settings-loading { padding:40px; text-align:center; }
.nav-settings-layout { display:grid; grid-template-columns:250px minmax(0,1fr); gap:16px; align-items:start; }
.nav-settings-list,.nav-settings-editor { background:#fff; border:1px solid #e8ebf0; border-radius:14px; box-shadow:0 10px 28px rgba(26,35,55,.045); }
.nav-settings-list { padding:13px; position:sticky; top:78px; max-height:calc(100vh - 100px); overflow:auto; }.list-title { padding:5px 4px 12px; font-size:13px; }
.list-title button,.child-heading button { color:#8f2535; border-color:#f0dadd; background:#fff7f8; }
.menu-select { display:flex; width:100%; text-align:left; align-items:center; gap:10px; margin:4px 0; padding:10px !important; border:0 !important; }
.menu-select.selected { background:#fff0f2; color:#8f2535; }.menu-select-icon { width:30px; height:30px; border-radius:8px; display:grid; place-items:center; background:#f4f5f7; }
.menu-select strong,.menu-select small { display:block; }.menu-select strong { font-size:12px; }.menu-select small { margin-top:3px; color:#8892a2; font-size:10px; }.menu-select-icon svg { width:17px; height:17px; }
.nav-settings-editor { min-width:0; padding:20px; }.editor-head { padding-bottom:17px; border-bottom:1px solid #edf0f4; }.order-actions button { padding:5px 9px; }.order-actions .danger { color:#a3323f; }
.editor-grid { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:14px; padding:18px 0; }
.editor-grid label { display:flex; flex-direction:column; gap:6px; color:#68758a; font-size:11px; font-weight:600; }.editor-grid label.wide { grid-column:1/-1; }
.editor-grid input:not([type=checkbox]),.editor-grid select { width:100%; min-width:0; height:36px; padding:0 10px; border:1px solid #dfe5ee; border-radius:8px; background:#fff; color:#263348; font-size:12px; }
.icon-select { display:flex; align-items:center; gap:8px; }.icon-select svg { width:20px; height:20px; flex:0 0 20px; color:#922b3b; }
.editor-grid label.check { flex-direction:row; align-items:center; }.editor-grid input[type=checkbox] { accent-color:#8f2535; }
.child-heading { border-top:1px solid #edf0f4; padding:17px 0 10px; }.child-editor { margin:10px 0; padding:14px 16px 0; border:1px solid #e8ebf0; border-radius:10px; background:#fcfdff; }.child-top { color:#344158; font-size:12px; }
@media(max-width:900px) { .nav-settings-layout { grid-template-columns:1fr; }.nav-settings-list { position:static; max-height:none; display:flex; flex-wrap:wrap; gap:4px; }.list-title { width:100%; }.menu-select { width:auto; }.nav-settings-intro { align-items:flex-start; } }
@media(max-width:620px) { .nav-settings-intro { flex-direction:column; }.editor-grid { grid-template-columns:1fr; }.editor-grid label.wide { grid-column:auto; }.child-top,.editor-head { align-items:flex-start; flex-direction:column; } }
</style>
